// Item library evaluate codes for /roads-sewers/pos dev-mode display — mirrors
// roadsEvaluateCodesMapping.ts's description -> code lookup pattern. POS.tsx's
// Evaluate Code column looks its rows up here by description rather than
// hardcoding codes inline, so codes stay in one place as more are added.

export interface PosEvaluateCodeItem {
  description: string;
  code: string;
}

export const POS_EVALUATE_CODES: PosEvaluateCodeItem[] = [
  { description: 'Geotextile membrane', code: 'GWALP014' },
  { description: 'Level and compact formation', code: 'ALP035' },
  { description: 'Timber Edgings 150 x 50', code: 'GWALP032' },
  { description: 'Sweep off and apply tack coat prior to final surfacing', code: 'ALP055' },
  { description: 'Edgings 150 x 50', code: 'ALP027' },
];

// POS_CONFIGURABLE_FIELDS rows (posRowDefs.ts) embed a live-configured mm value
// (or the "xxx"/"xx" placeholder before configured) instead of fixed text —
// matched by pattern rather than exact text, same convention as
// roadsEvaluateCodesMapping.ts's OMIT_TYPE1_PATTERN etc.
const NO_DIG_LAYER_PATTERN = /^(\d+|xxx)mm thick 'No Dig' layer to gardens in 6F2$/;
const IMPORTED_SUBSOIL_PATTERN = /^Spread and level (\d+|xxx)mm thick imported clean cap subsoil$/;
const SITE_WON_SUBSOIL_PATTERN = /^Spread and level (\d+|xxx)mm thick site won clean cap subsoil$/;
const TOPSOIL_SITE_WON_PATTERN = /^Spread and level (\d+|xxx)mm thick top soil - site won$/;
const TOPSOIL_IMPORTED_PATTERN = /^Spread and level (\d+|xxx)mm thick top soil - imported$/;
// Shared by POS Gravel Path's "gravelMOT" and POS Macadam Path's "macMOT" fields —
// both use the same "<mm>mm thick MOT Type 1" template.
const MOT_TYPE1_PATTERN = /^(\d+|xxx)mm thick MOT Type 1$/;
const GRAVEL_SURFACE_PATTERN = /^(\d+|xx)mm thick Breedon Wayfarer bound gravel surfaced pathway$/;

// POS Macadam Path's AC20 DBM binder course / AC6 DBM surface course rows are
// "Configurator" fields — same live-configured-thickness idea, but the code
// depends on which of a fixed set of thicknesses is picked (POS_CONFIGURABLE_FIELDS'
// macAC20/macAC6 options: 50/60/75/80/100 and 20/25/30 respectively), mirroring
// /groundworks/plot-drives' PlotDrives.tsx LABEL_TO_ALP_CODE_MAP (binder) and
// matchSurfaceCourseToEvaluateCode (surface) — same codes, reused here for POS's
// own wording ("<mm>mm thick AC20/AC6 DBM ... course" vs Plot Drives' own labels).
const AC20_BINDER_PATTERN = /^(\d+)mm thick AC20 DBM binder course$/;
const AC20_BINDER_CODE_BY_MM: Record<number, string> = {
  50: 'ALP043',
  60: 'ALP044',
  75: 'ALP045',
  80: 'ALP046',
  100: 'ALP047',
};

const AC6_SURFACE_PATTERN = /^(\d+)mm thick AC6 DBM surface course$/;
const AC6_SURFACE_CODE_BY_MM: Record<number, string> = {
  20: 'ALP048',
  25: 'ALP049',
  30: 'ALP051',
};

export function findPosEvaluateCode(description: string): string | undefined {
  const fixed = POS_EVALUATE_CODES.find((i) => i.description === description)?.code;
  if (fixed) return fixed;
  if (NO_DIG_LAYER_PATTERN.test(description)) return 'GWALPGW033';
  if (IMPORTED_SUBSOIL_PATTERN.test(description)) return 'GWALPGW036';
  if (SITE_WON_SUBSOIL_PATTERN.test(description)) return 'GWALPGW034';
  if (TOPSOIL_SITE_WON_PATTERN.test(description)) return 'GWALPGW002';
  if (TOPSOIL_IMPORTED_PATTERN.test(description)) return 'GWALPGW035';
  if (MOT_TYPE1_PATTERN.test(description)) return 'ALP017c';
  if (GRAVEL_SURFACE_PATTERN.test(description)) return 'GWALPPOS002';
  const binderMatch = description.match(AC20_BINDER_PATTERN);
  if (binderMatch) return AC20_BINDER_CODE_BY_MM[Number(binderMatch[1])];
  const surfaceMatch = description.match(AC6_SURFACE_PATTERN);
  if (surfaceMatch) return AC6_SURFACE_CODE_BY_MM[Number(surfaceMatch[1])];
  return undefined;
}
